/**
 * Albero di navigazione dell'alberatura, per **scegliere** un obbligo.
 *
 * E' un componente distinto da quello della pagina «Alberatura», e non una sua
 * generalizzazione: li' ogni riga porta i comandi di amministrazione del nodo
 * (applicabilita', estensione, rimozione) ed e' una pagina di configurazione;
 * qui una riga e' una destinazione e l'unico gesto e' sceglierla. Farne un solo
 * componente avrebbe richiesto una render-prop per i comandi di riga, cioe' piu'
 * indirezione di quanta ne risolva.
 *
 * Cio' che invece **e'** condiviso sta gia' in `lib/alberatura.ts`:
 * `discendentiDiNodo` per la visita, `catenePerId` e `percorsoNodo` per il
 * percorso leggibile. Quella e' la logica che non puo' divergere.
 *
 * Accessibilita': come nella pagina «Alberatura», l'albero e' un elenco vero con
 * `aria-level`, non un `role="tree"`. Il pattern ARIA tree presuppone la
 * navigazione con le frecce e un solo elemento nel giro di tabulazione; qui ogni
 * riga e' un pulsante, e un elenco annunciato con i propri livelli e' piu'
 * onesto di un tree a meta'.
 */

import { useMemo, useState } from "react";
import type { Nodo } from "../api/types";
import { IconaChevron } from "./Icone";
import { Chip } from "./primitive";
import { discendentiDiNodo } from "../lib/alberatura";

function sezioneContenente(nodi: readonly Nodo[], idNodo: string): string | null {
  for (const sezione of nodi) {
    if (sezione.id_nodo === idNodo) return sezione.id_nodo;
    if (discendentiDiNodo(sezione).some(({ nodo }) => nodo.id_nodo === idNodo)) return sezione.id_nodo;
  }
  return null;
}

export function AlberoNodi({
  nodi,
  selezionato,
  onSeleziona,
  selezionabile,
  etichettaNonSelezionabile = "Non selezionabile",
}: {
  /** Le sezioni di primo livello, con i figli annidati. */
  nodi: readonly Nodo[];
  /** Identificativo del nodo attualmente scelto. */
  selezionato: string | null;
  onSeleziona: (nodo: Nodo) => void;
  /** Quali nodi si possono scegliere; gli altri restano visibili come contesto. */
  selezionabile: (nodo: Nodo) => boolean;
  etichettaNonSelezionabile?: string;
}) {
  const [aperti, setAperti] = useState<Set<string>>(new Set());

  /**
   * Quando la scelta arriva da fuori (il parametro `nodo` dell'URL, un
   * collegamento dell'assistente) la sezione che la contiene si apre da sola:
   * un obbligo scelto ma nascosto in una sezione chiusa sembrerebbe non scelto.
   */
  const [ultimoSelezionato, setUltimoSelezionato] = useState<string | null>(null);
  if (selezionato !== ultimoSelezionato) {
    setUltimoSelezionato(selezionato);
    const sezione = selezionato ? sezioneContenente(nodi, selezionato) : null;
    if (sezione && !aperti.has(sezione)) setAperti(new Set([...aperti, sezione]));
  }

  /**
   * Le sezioni che non contengono alcun nodo selezionabile restano nell'albero
   * ma si annunciano come vuote: nasconderle farebbe sparire dall'elenco
   * pezzi dell'alberatura ANAC, e un redattore che cerca «Bilanci» senza
   * trovarlo non saprebbe se non e' assegnato o se non esiste.
   */
  const conteggi = useMemo(() => {
    const mappa = new Map<string, number>();
    for (const sezione of nodi) {
      const dentro = discendentiDiNodo(sezione).filter(({ nodo }) =>
        selezionabile(nodo),
      ).length;
      mappa.set(sezione.id_nodo, dentro + (selezionabile(sezione) ? 1 : 0));
    }
    return mappa;
  }, [nodi, selezionabile]);

  const commuta = (idNodo: string) => {
    setAperti((correnti) => {
      const successivi = new Set(correnti);
      if (successivi.has(idNodo)) successivi.delete(idNodo);
      else successivi.add(idNodo);
      return successivi;
    });
  };

  return (
    <ul className="albero" aria-label="Alberatura dell'Amministrazione trasparente">
      {nodi.map((sezione) => {
        const aperta = aperti.has(sezione.id_nodo);
        const dentro = conteggi.get(sezione.id_nodo) ?? 0;

        return (
          <li key={sezione.id_nodo} className="albero__nodo" aria-level={1}>
            <div className="albero__riga">
              <button
                type="button"
                className="albero__interruttore-apertura"
                onClick={() => commuta(sezione.id_nodo)}
                aria-expanded={aperta}
                aria-controls={`scelta-figli-${sezione.id_nodo}`}
                aria-label={`${aperta ? "Chiudi" : "Apri"} ${sezione.denominazione}`}
              >
                <IconaChevron
                  style={{
                    transform: aperta ? "rotate(90deg)" : "none",
                    transition: "transform 140ms",
                  }}
                />
              </button>

              <div className="albero__corpo">
                <div className="albero__denominazione">{sezione.denominazione}</div>
              </div>

              <Chip tono={dentro ? "neutro" : "attenzione"} pallino={false}>
                {dentro} {dentro === 1 ? "obbligo" : "obblighi"}
              </Chip>
            </div>

            {/* Il contenitore esiste anche da chiuso: `aria-controls` del
                pulsante deve puntare a un elemento presente nel documento. */}
            <ul
              id={`scelta-figli-${sezione.id_nodo}`}
              className="albero__discendenti"
              aria-label={`Sottosezioni e obblighi di ${sezione.denominazione}`}
            >
              {aperta
                ? discendentiDiNodo(sezione).map(({ nodo, profondita, livelloAria }) => {
                    const scegliibile = selezionabile(nodo);
                    const corrente = nodo.id_nodo === selezionato;
                    return (
                      <li
                        key={nodo.id_nodo}
                        className={`albero__riga albero__riga--figlio${
                          corrente ? " albero__riga--corrente" : ""
                        }`}
                        style={{ paddingLeft: `${profondita * 1.25}rem` }}
                        aria-level={livelloAria}
                      >
                        {scegliibile ? (
                          <button
                            type="button"
                            className="albero__scelta"
                            aria-current={corrente ? "true" : undefined}
                            onClick={() => onSeleziona(nodo)}
                          >
                            <span className="albero__denominazione">{nodo.denominazione}</span>
                          </button>
                        ) : (
                          <div className="albero__corpo albero__corpo--inerte">
                            <div className="albero__denominazione">{nodo.denominazione}</div>
                            <div className="albero__meta">{etichettaNonSelezionabile}</div>
                          </div>
                        )}
                      </li>
                    );
                  })
                : null}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}
